import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { CreditCard, Coins, ArrowLeft } from "lucide-react";

const PACKAGE = {
  id: "credit_publication",
  name: "Crédit de publication d'offre",
  price: "49,00 $ CAD",
  desc: "Publiez une offre d'emploi sur NEXA, visible par la banque internationale de candidats.",
};

export default function Billing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(0);
  const [freePosting, setFreePosting] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState("");
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  const load = useCallback(async () => {
    try {
      const [creditsRes, txRes] = await Promise.all([
        api.get("/payments/credits"),
        api.get("/payments/mine"),
      ]);
      setCredits(creditsRes.data.credits);
      setFreePosting(creditsRes.data.free_posting);
      setTransactions(txRes.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const buy = async () => {
    setError("");
    setBuying(true);
    try {
      const res = await api.post("/payments/checkout", {
        package_id: PACKAGE.id,
        quantity: 1,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.approval_url;
    } catch (err) {
      setError(formatApiError(err));
      setBuying(false);
    }
  };

  if (!user) return null;

  return (
    <div data-testid="billing-page" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} data-testid="billing-logo-link" aria-label="Retour au tableau de bord">
            <Logo />
          </button>
          <button
            data-testid="billing-back-button"
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-navy border border-slate-300 rounded-lg px-4 py-2 hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Tableau de bord
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        <h1 className="text-2xl font-bold text-brand-navy">Facturation</h1>

        {error && (
          <div data-testid="billing-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Coins className="w-5 h-5 text-brand-turquoise" />
              <h2 className="font-semibold text-brand-navy">Mes crédits</h2>
            </div>
            <p data-testid="credits-balance" className="text-4xl font-extrabold text-brand-navy mb-2">{credits}</p>
            <p className="text-sm text-slate-500">
              {freePosting
                ? "La publication est actuellement gratuite — vos crédits seront utilisables lorsque la publication payante sera activée."
                : "Chaque publication d'offre consomme 1 crédit."}
            </p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-brand-blue" />
              <h2 className="font-semibold text-brand-navy">Acheter des crédits</h2>
            </div>
            <p className="font-medium text-brand-navy text-sm">{PACKAGE.name}</p>
            <p className="text-sm text-slate-500 mb-4">{PACKAGE.desc}</p>
            <div className="flex items-center justify-between mb-5">
              <span className="text-2xl font-extrabold text-brand-navy">{PACKAGE.price}</span>
              <span className="text-xs text-slate-400">Paiement sécurisé par PayPal</span>
            </div>
            <button
              data-testid="buy-credit-button"
              onClick={buy}
              disabled={buying}
              className="w-full bg-brand-blue text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-navy transition-colors duration-200 disabled:opacity-50"
            >
              {buying ? "Redirection vers PayPal…" : "Acheter avec PayPal"}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-5 border-b border-slate-100">
            <h2 className="font-semibold text-brand-navy">Historique des transactions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="px-5 py-3 font-medium">Forfait</th>
                  <th className="px-5 py-3 font-medium">Montant</th>
                  <th className="px-5 py-3 font-medium">Statut</th>
                  <th className="px-5 py-3 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.transaction_id} data-testid={`transaction-${t.transaction_id}`} className="border-b border-slate-50">
                    <td className="px-5 py-3 text-brand-navy font-medium">{t.package_name}</td>
                    <td className="px-5 py-3 text-slate-600">{t.amount} {t.currency}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${
                        t.payment_status === "paid" ? "bg-green-50 text-green-700"
                        : t.payment_status === "pending" ? "bg-amber-50 text-amber-700"
                        : "bg-slate-100 text-slate-500"
                      }`}>
                        {t.payment_status === "paid" ? "Payé" : t.payment_status === "pending" ? "En attente" : t.payment_status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-500">
                      {t.created_at ? new Date(t.created_at).toLocaleDateString("fr-CA") : "—"}
                    </td>
                  </tr>
                ))}
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan={4} data-testid="transactions-empty" className="px-5 py-8 text-center text-slate-400">
                      Aucune transaction pour le moment.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
